import * as React from "react";
import { cn } from "@/lib/utils";

interface AnimationProps {
    children: React.ReactNode;
    className?: string;
    /**
     * If true, the animation will play. If false, the element will not render.
     * Useful for conditional rendering with animations.
     */
    show: boolean;
    /**
     * If true, the animation will play on initial mount. If false (default),
     * the element appears immediately without animation on first render if show is true.
     */
    animateOnMount?: boolean;
    /**
     * Delay before animation starts (in ms)
     */
    delay?: number;
    /**
     * Duration of the animation (in ms)
     */
    duration?: number;
    /**
     * Callback fired when animation completes
     */
    onAnimationEnd?: () => void;
}

/**
 * Base animation wrapper that handles both enter and exit animations
 */
const AnimationWrapper = React.forwardRef<
    HTMLDivElement,
    AnimationProps & {
        enterClassName: string;
        exitClassName: string;
        durationClassName?: string;
    }
>(
    (
        {
            children,
            className,
            show,
            animateOnMount = false,
            onAnimationEnd,
            enterClassName,
            exitClassName,
            durationClassName = "duration-500",
        },
        ref,
    ) => {
        const [shouldRender, setShouldRender] = React.useState(show);
        const [isExiting, setIsExiting] = React.useState(false);
        const [initialShow] = React.useState(show);
        const [hasShownChanged, setHasShownChanged] = React.useState(false);

        React.useEffect(() => {
            if (show) {
                setShouldRender(true);
                setIsExiting(false);
            } else {
                setIsExiting(true);
                // Fallback only: unmount is normally driven by the real
                // `animationend` event (see handleAnimationEnd below). This
                // timer just guards against an exit animation never firing
                // one (e.g. animate-out classes not applying for some reason).
                const timer = setTimeout(
                    () => {
                        setShouldRender(false);
                    },
                    parseInt(durationClassName.split("-").at(1) as string) ??
                        500,
                );
                return () => clearTimeout(timer);
            }
        }, [durationClassName, show]);

        if (initialShow !== show && !hasShownChanged) {
            setHasShownChanged(true);
        }

        const shouldAnimate = animateOnMount || hasShownChanged || isExiting;

        const handleAnimationEnd = () => {
            onAnimationEnd?.();
            if (isExiting) {
                setShouldRender(false);
            }
        };

        if (!shouldRender) return null;
        return (
            <div
                ref={ref}
                className={cn(
                    shouldAnimate
                        ? isExiting
                            ? exitClassName
                            : enterClassName
                        : "",
                    durationClassName,
                    className,
                )}
                onAnimationEnd={handleAnimationEnd}
            >
                {children}
            </div>
        );
    },
);
AnimationWrapper.displayName = "AnimationWrapper";

/**
 * Fade animation - element fades in and out
 */
export const AnimateFade = React.forwardRef<HTMLDivElement, AnimationProps>(
    (props, ref) => (
        <AnimationWrapper
            {...props}
            ref={ref}
            enterClassName={cn("animate-in fade-in duration-500")}
            exitClassName={cn("animate-out fade-out")}
        />
    ),
);
AnimateFade.displayName = "Fade";

/**
 * Slide from top - element slides down when entering, up when exiting
 */
export const AnimateSlideFromTop = React.forwardRef<
    HTMLDivElement,
    AnimationProps
>((props, ref) => (
    <AnimationWrapper
        {...props}
        ref={ref}
        enterClassName={cn("animate-in slide-in-from-top-4 fade-in")}
        exitClassName={cn("animate-out slide-out-to-top-4 fade-out")}
    />
));
AnimateSlideFromTop.displayName = "SlideFromTop";

/**
 * Slide from bottom - element slides up when entering, down when exiting
 */
export const AnimateSlideFromBottom = React.forwardRef<
    HTMLDivElement,
    AnimationProps
>((props, ref) => (
    <AnimationWrapper
        {...props}
        ref={ref}
        enterClassName={cn("animate-in slide-in-from-bottom-4 fade-in")}
        exitClassName={cn("animate-out slide-out-to-bottom-4 fade-out")}
    />
));
AnimateSlideFromBottom.displayName = "SlideFromBottom";

/**
 * Slide from left - element slides in from left when entering, out to left when exiting
 */
export const AnimateSlideFromLeft = React.forwardRef<
    HTMLDivElement,
    AnimationProps
>((props, ref) => (
    <AnimationWrapper
        {...props}
        ref={ref}
        enterClassName={cn("animate-in slide-in-from-left-4 fade-in")}
        exitClassName={cn("animate-out slide-out-to-left-4 fade-out")}
    />
));
AnimateSlideFromLeft.displayName = "SlideFromLeft";

/**
 * Slide from right - element slides in from right when entering, out to right when exiting
 */
export const AnimateSlideFromRight = React.forwardRef<
    HTMLDivElement,
    AnimationProps
>((props, ref) => (
    <AnimationWrapper
        {...props}
        ref={ref}
        enterClassName={cn("animate-in slide-in-from-right-4 fade-in")}
        exitClassName={cn("animate-out slide-out-to-right-4 fade-out")}
    />
));
AnimateSlideFromRight.displayName = "SlideFromRight";

/**
 * Scale up - element scales up when entering, reverses when exiting
 * Good for emphasis or modal-like content
 */
export const AnimateScaleUp = React.forwardRef<HTMLDivElement, AnimationProps>(
    (props, ref) => (
        <AnimationWrapper
            {...props}
            ref={ref}
            enterClassName={cn("animate-in zoom-in-95 fade-in")}
            exitClassName={cn("animate-out zoom-out-95 fade-out")}
        />
    ),
);
AnimateScaleUp.displayName = "ScaleUp";

/**
 * Custom animation component - allows full control over enter and exit animations
 */
interface CustomAnimationProps extends AnimationProps {
    enterClass?: string;
    exitClass?: string;
}

export const AnimateCustomAnimation = React.forwardRef<
    HTMLDivElement,
    CustomAnimationProps
>(
    (
        {
            enterClass = "animate-in fade-in",
            exitClass = "animate-out fade-out",
            ...props
        },
        ref,
    ) => (
        <AnimationWrapper
            {...props}
            ref={ref}
            enterClassName={cn(enterClass)}
            exitClassName={cn(exitClass)}
        />
    ),
);
AnimateCustomAnimation.displayName = "CustomAnimation";
